import React, { useEffect, useState, useCallback } from 'react';
import { Autocomplete, TextField, CircularProgress } from '@mui/material';
import toast from 'react-hot-toast';
import { useTheme } from '../../context/ThemeContext';
import { financeApi, PaymentMode } from '../../api/financeApi';

// Module-level cache so multiple instances on the same page don't refetch.
// We treat the list as small and infrequently mutated; cache invalidates on
// any create/update returning new data via refresh().
let cachedModes: PaymentMode[] | null = null;
const cacheSubscribers = new Set<(modes: PaymentMode[]) => void>();

const setCache = (modes: PaymentMode[]) => {
  cachedModes = modes;
  cacheSubscribers.forEach((cb) => cb(modes));
};

const fetchModes = async (): Promise<PaymentMode[]> => {
  const res = await financeApi.getPaymentModes();
  const data = Array.isArray(res.data) ? res.data : [];
  setCache(data);
  return data;
};

const FALLBACK_MODES: PaymentMode[] = [
  { id: -1, name: 'CASH', displayName: 'Cash', isDefault: true, isActive: true, sortOrder: 0 },
  { id: -2, name: 'BANK_TRANSFER', displayName: 'Bank Transfer', isDefault: true, isActive: true, sortOrder: 1 },
  { id: -3, name: 'UPI', displayName: 'UPI', isDefault: true, isActive: true, sortOrder: 2 },
  { id: -4, name: 'CARD', displayName: 'Card', isDefault: true, isActive: true, sortOrder: 3 },
  { id: -5, name: 'CREDIT', displayName: 'Credit', isDefault: true, isActive: true, sortOrder: 4 },
];

export interface PaymentModeSelectProps {
  value: string;
  onChange: (modeName: string) => void;
  label?: string;
  fullWidth?: boolean;
  size?: 'small' | 'medium';
  sx?: Record<string, unknown>;
  disabled?: boolean;
  /** Optional override for the input testid */
  'data-testid'?: string;
}

const toCanonical = (raw: string): string =>
  raw.trim().toUpperCase().replace(/[^A-Z0-9]+/g, '_').replace(/^_+|_+$/g, '');

const PaymentModeSelect: React.FC<PaymentModeSelectProps> = ({
  value,
  onChange,
  label = 'Payment Mode',
  fullWidth = true,
  size = 'medium',
  sx,
  disabled = false,
  'data-testid': testId,
}) => {
  const { theme } = useTheme();
  const [modes, setModes] = useState<PaymentMode[]>(cachedModes || FALLBACK_MODES);
  const [loading, setLoading] = useState(!cachedModes);
  const [creating, setCreating] = useState(false);
  const [inputValue, setInputValue] = useState('');

  // Subscribe to cache updates so other PaymentModeSelect instances see new modes.
  useEffect(() => {
    const sub = (next: PaymentMode[]) => setModes(next);
    cacheSubscribers.add(sub);
    return () => {
      cacheSubscribers.delete(sub);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (!cachedModes) {
      fetchModes()
        .then((data) => {
          if (!cancelled) setModes(data);
        })
        .catch(() => {
          // Fallback to defaults silently; API may not be deployed yet.
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    } else {
      setLoading(false);
    }
    return () => {
      cancelled = true;
    };
  }, []);

  // Find the option matching the current `value` (by canonical name).
  const activeModes = modes.filter((m) => m.isActive);
  const selectedOption =
    activeModes.find((m) => m.name === value) ||
    modes.find((m) => m.name === value) ||
    (value
      ? { id: -999, name: value, displayName: value, isDefault: false, isActive: true, sortOrder: 999 }
      : null);

  const handleCreate = useCallback(
    async (rawInput: string) => {
      const trimmed = rawInput.trim();
      if (!trimmed) return;
      const canonical = toCanonical(trimmed);
      if (!canonical) return;

      // If a mode with this canonical name already exists, just select it.
      const existing = modes.find((m) => m.name === canonical);
      if (existing) {
        onChange(existing.name);
        return;
      }

      setCreating(true);
      try {
        const res = await financeApi.createPaymentMode({
          name: canonical,
          displayName: trimmed,
        });
        const created = res.data;
        const next = [...modes.filter((m) => m.name !== created.name), created].sort(
          (a, b) => a.sortOrder - b.sortOrder,
        );
        setCache(next);
        onChange(created.name);
        toast.success(`Added payment mode "${created.displayName}"`);
      } catch (err: unknown) {
        const status = (err as { response?: { status?: number } })?.response?.status;
        if (status === 409) {
          // Duplicate — refresh and select existing.
          try {
            const fresh = await fetchModes();
            const match = fresh.find((m) => m.name === canonical);
            if (match) onChange(match.name);
          } catch {
            /* ignore */
          }
        } else {
          const msg =
            (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
            'Failed to add payment mode';
          toast.error(msg);
        }
      } finally {
        setCreating(false);
      }
    },
    [modes, onChange],
  );

  const inputSx = {
    '& .MuiOutlinedInput-root': {
      backgroundColor: theme.components?.input?.background || '#FFFFFF',
      borderRadius: '10px',
      '& fieldset': { borderColor: theme.components?.input?.border || '#DDD8D2' },
      '&:hover fieldset': { borderColor: theme.primary },
      '&.Mui-focused fieldset': {
        borderColor: theme.components?.input?.focus || theme.primary,
      },
    },
    '& .MuiInputLabel-root': { color: theme.gray?.[500] || '#666' },
    '& .MuiInputLabel-root.Mui-focused': { color: theme.primary },
    '& .MuiInputBase-input': { color: theme.text },
    ...(sx || {}),
  } as Record<string, unknown>;

  return (
    <Autocomplete
      freeSolo
      fullWidth={fullWidth}
      size={size}
      disabled={disabled || creating}
      options={activeModes}
      value={selectedOption as PaymentMode | null}
      inputValue={inputValue || (selectedOption ? selectedOption.displayName : '')}
      onInputChange={(_e, val, reason) => {
        if (reason === 'input') setInputValue(val);
        if (reason === 'reset') setInputValue('');
      }}
      isOptionEqualToValue={(opt, val) => {
        if (!opt || !val) return false;
        if (typeof opt === 'string' || typeof val === 'string') return opt === val;
        return opt.name === val.name;
      }}
      getOptionLabel={(opt) => {
        if (typeof opt === 'string') return opt;
        return opt?.displayName || opt?.name || '';
      }}
      onChange={(_e, newVal) => {
        if (!newVal) {
          onChange('');
          return;
        }
        if (typeof newVal === 'string') {
          // Free-text entry (Enter on a string) — try create.
          handleCreate(newVal);
          return;
        }
        onChange(newVal.name);
      }}
      onBlur={() => {
        // If user typed a brand-new value and tabbed away without picking it
        // from the dropdown, treat blur as confirmation.
        const trimmed = inputValue.trim();
        if (!trimmed) return;
        const canonical = toCanonical(trimmed);
        const existing = modes.find(
          (m) => m.name === canonical || m.displayName.toLowerCase() === trimmed.toLowerCase(),
        );
        if (existing) {
          if (existing.name !== value) onChange(existing.name);
        } else {
          handleCreate(trimmed);
        }
        setInputValue('');
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder="Type or select"
          inputProps={{ ...params.inputProps, 'data-testid': testId }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading || creating ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
          sx={inputSx}
        />
      )}
    />
  );
};

export default PaymentModeSelect;
